import { useContext, useEffect, useState } from "react";

import { GetStaticPaths, GetStaticProps } from "next/types";

import { BillboardLoading } from "pages/[tenant]/browse/components/BillboardLoading";
import { useBillboardOrganizationContent } from "pages/[tenant]/browse/hooks/useBillboardOrganizationContent";
import {
  getHighlights,
  getSection,
  getSectionContent,
} from "pages/[tenant]/browse/utils/requests";

import BannerWarning from "shared/components/atoms/BannerWarning";
import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { useOrganizationConfig } from "shared/hooks/useOrganizationConfig";
import AuthContext from "shared/providers/context/AuthContext";
import { IBillboardHighlight, IBillboardSection } from "shared/types/billboard";
import { usePixel } from "shared/utils/hooks/Pixel";

import { captureException, captureMessage } from "@sentry/nextjs";

import BillboardPage from "./components/BillboardPage";
import { ISectionContent } from "./types";

interface IBillboardProps {
  highlights: IBillboardHighlight[];
  sections: IBillboardSection[];
  sectionContent: ISectionContent[][];
}

const Billboard = ({
  highlights,
  sections,
  sectionContent,
}: IBillboardProps) => {
  const { setTrackPageView } = usePixel();
  const { loggedUser } = useContext(AuthContext);
  const { getOrganizationFromCookies, organizationIsMain } =
    useOrganizationConfig();

  const organizationInCookie = getOrganizationFromCookies();
  const shouldShowOrganizationBillboardContent =
    !!organizationInCookie && !organizationIsMain;

  const {
    handleLoadOrganizationBillboardContent,
    organizationBillboardContent,
    requestsProgressRateConclusion,
    isLoadingOrganizationBillboardContent,
  } = useBillboardOrganizationContent();

  const [updatedSections, setUpdatedSections] = useState(sections);
  const [updatedSectionContent, setUpdatedSectionContent] =
    useState<ISectionContent[][]>(sectionContent);

  useEffect(() => {
    if (shouldShowOrganizationBillboardContent)
      handleLoadOrganizationBillboardContent();
  }, [shouldShowOrganizationBillboardContent]);

  useEffect(() => {
    try {
      setTrackPageView(loggedUser);
    } catch (error) {
      console.log("Erro (Billboard::useEffect): ", error);
      captureException(error);
      captureMessage("Erro (Billboard::useEffect)");
    }
  }, []);

  useEffect(() => {
    const fetchMyContents = async () => {
      try {
        const myContentsSections = sections?.filter(
          (section) => section?.type === "my_contents",
        );

        if (myContentsSections?.length > 0) {
          const newSectionContents = await Promise.all(
            myContentsSections?.map(async (section) => {
              const content = await getSectionContent({
                sectionId: section.id,
              });
              return { sectionId: section?.id, content };
            }),
          );

          const updated = sections?.map((section) => {
            if (section?.type === "my_contents") {
              const matchedContent = newSectionContents.find(
                (item) => item?.sectionId === section?.id,
              );
              return {
                ...section,
                content: matchedContent?.content || [],
              };
            }
            return section;
          });

          setUpdatedSections(updated);

          setUpdatedSectionContent((prevContent) => {
            const updatedContent = [...prevContent];

            newSectionContents.forEach(({ sectionId, content }) => {
              const sectionIndex = updated?.findIndex(
                (s) => s.id === sectionId,
              );
              if (sectionIndex !== -1) {
                updatedContent[sectionIndex] = content;
              }
            });

            return updatedContent;
          });
        }
      } catch (error) {
        console.log("Erro (fetchMyContents): ", error);
      }
    };

    if (loggedUser) fetchMyContents();
  }, [sections, loggedUser]);

  const hasContentToShow = shouldShowOrganizationBillboardContent
    ? !!organizationBillboardContent?.sections?.length ||
      !!organizationBillboardContent?.highlights?.length
    : !!updatedSections?.length && !!highlights?.length;

  const billboardPageProps = shouldShowOrganizationBillboardContent
    ? {
        highlights: organizationBillboardContent?.highlights,
        sections: organizationBillboardContent?.sections,
        sectionContent: organizationBillboardContent?.sectionContent,
      }
    : {
        highlights,
        sections: updatedSections,
        sectionContent: updatedSectionContent,
      };

  if (!hasContentToShow && !isLoadingOrganizationBillboardContent)
    return (
      <ComponentErrorBoundary>
        <BannerWarning />
      </ComponentErrorBoundary>
    );

  return (
    <>
      <BillboardPage
        highlights={billboardPageProps?.highlights}
        sections={billboardPageProps?.sections}
        sectionContent={billboardPageProps?.sectionContent}
      />

      {shouldShowOrganizationBillboardContent && (
        <ComponentErrorBoundary>
          <BillboardLoading progressRate={requestsProgressRateConclusion} />
        </ComponentErrorBoundary>
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async () => {
  const [highlights, sections] = await Promise.all([
    getHighlights(),
    getSection(),
  ]);

  const sectionContent = await Promise.all(
    sections.map((section) => getSectionContent({ sectionId: section?.id })),
  );

  return {
    props: {
      highlights,
      sections,
      sectionContent,
    },
    revalidate: 60 * 1,
  };
};

export default Billboard;
